/**
 * AiAnalysisModalHost：AI 分析弹窗宿主层（controller）。
 * - 该层负责“整张报表只挂载一个弹窗实例”的生命周期管理，避免多卡片并发打开导致会话状态相互污染。
 * - 该层聚焦会话编排（prepare / apply / discard）与外部事件桥接，不承载设置页/报告页的具体 UI 与表单逻辑。
 *
 * 为什么拆成 Host + AiSettings：
 * - Host 解决跨卡片共享弹窗、会话隔离和关闭清理等流程问题；
 * - AiSettings 解决弹窗内部的交互状态机（setting/report）、参数编辑和预览渲染。
 *
 * 是否可以合成一个文件：
 * - 技术上可以，但会把“跨卡片控制逻辑”和“弹窗内部 UI 逻辑”耦合在一起，导致职责过重、回归风险变高；
 * - 当前拆分更利于后续单独演进（例如替换弹窗 UI，不影响 Host 的会话编排）。
 */
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import type { IBuildAiAnalysisInputParams } from '../runAiAnalysis';
import type {
  IAiPromptInfo,
  IAiPromptOption,
  IAiAnalysisStreamingInput,
  IAiScopeTabItem,
} from '../types';
import type { IUseStreamingConversationResult } from '@dbl/core/global';

import AiAnalysisSettingsModal from '../AiSettings';

export interface IAiAnalysisModalHostProps {
  visible: boolean;
  activeCardId?: string;
  isEdit?: boolean;
  gameCode?: string;
  dashboardId?: string;
  scopeTabs: IAiScopeTabItem[];
  promptOptions: IAiPromptOption[];

  defaultSelectedApproachId?: number | string;
  popupContainer?: HTMLElement | null;
  initialPromptInfo?: IAiPromptInfo;

  onConfirmPromptInfo: (promptInfo: IAiPromptInfo) => void;
  onRequestClose: () => void;

  previewSession: IUseStreamingConversationResult<IAiAnalysisStreamingInput>;
  preparePreviewSession: () => void;
  runPreviewAnalysis: (params: IBuildAiAnalysisInputParams) => Promise<void>;
  onApplyToActiveCard: (promptInfo: IAiPromptInfo) => Promise<void>;
  discardPreviewSession: () => void;
  setAiMetricsForPreview: (promptId: string) => void;
  setAiMetricsForConfirm: (promptId: string) => void;
}

const AiAnalysisModalHostInner: React.FC<IAiAnalysisModalHostProps> = (props) => {
  const {
    visible,
    activeCardId,
    isEdit = false,
    gameCode = '',
    dashboardId = '',
    scopeTabs,
    promptOptions,
    defaultSelectedApproachId,
    popupContainer,
    initialPromptInfo,
    onConfirmPromptInfo,
    onRequestClose,
    previewSession,
    preparePreviewSession,
    runPreviewAnalysis,
    onApplyToActiveCard,
    discardPreviewSession,
    setAiMetricsForPreview,
    setAiMetricsForConfirm,
  } = props;
  const didApplyRef = useRef<boolean>(false);
  const wasVisibleRef = useRef<boolean>(false);

  useEffect(() => {
    if (visible && !wasVisibleRef.current) {
      didApplyRef.current = false;
      preparePreviewSession();
    }
    wasVisibleRef.current = visible;
  }, [preparePreviewSession, visible]);

  const handleClose = useCallback(() => {
    if (!didApplyRef.current) {
      discardPreviewSession();
    }
    didApplyRef.current = false;
    onRequestClose();
  }, [discardPreviewSession, onRequestClose]);

  const handleRunPreviewAnalysis = useCallback(
    async (params: IBuildAiAnalysisInputParams) => {
      await runPreviewAnalysis(params);
    },
    [runPreviewAnalysis],
  );

  const handleConfirmPromptInfo = useCallback(
    (promptInfo: IAiPromptInfo) => {
      onConfirmPromptInfo(promptInfo);
    },
    [onConfirmPromptInfo],
  );

  const handleApplyAndExit = useCallback(
    async (promptInfo: IAiPromptInfo) => {
      didApplyRef.current = true;
      await onApplyToActiveCard(promptInfo);
    },
    [onApplyToActiveCard],
  );

  const modalCardId = useMemo(() => String(activeCardId ?? ''), [activeCardId]);

  return (
    <AiAnalysisSettingsModal
      key={modalCardId}
      visible={visible}
      onClose={handleClose}
      isEdit={isEdit}
      gameCode={gameCode}
      dashboardId={dashboardId}
      activeCardId={modalCardId}
      scopeTabs={scopeTabs}
      promptOptions={promptOptions}
      defaultSelectedApproachId={defaultSelectedApproachId}
      popupContainer={popupContainer}
      initialPromptInfo={initialPromptInfo}
      onConfirmPromptInfo={handleConfirmPromptInfo}
      reportSession={previewSession}
      onRunPreviewAnalysis={handleRunPreviewAnalysis}
      onApplyAndExit={handleApplyAndExit}
      onSetAiMetricsForPreview={setAiMetricsForPreview}
      onSetAiMetricsForConfirm={setAiMetricsForConfirm}
    />
  );
};

const AiAnalysisModalHost = memo(AiAnalysisModalHostInner);
AiAnalysisModalHost.displayName = 'AiAnalysisModalHost';

export default AiAnalysisModalHost;

